import React from 'react';
import PropTypes from 'prop-types';

import { get } from 'lodash';

import { typography, color } from 'styled-system';

import styled, { css } from 'styled-components';

const TYPOGRAPHY_WEIGHTS = ['regular', 'semi-bold', 'light'];
const TYPOGRAPHY_ELEMENTS = ['heading', 'body', 'interactive', 'helper'];
const TYPOGRAPHY_FONTS = ['regular', 'serif'];
const TYPOGRAPHY_COMMON_PROPS = {
    /** Design System element level */
    level: PropTypes.number,
    /** Shorthand for `level` prop */
    lv: PropTypes.number,
    weight: PropTypes.oneOf(TYPOGRAPHY_WEIGHTS),
};

const Typography = styled.div`
    ${typography}
    ${color}

    margin: 0;

    ${({ theme, element }) => css`
        &:hover {
            color: ${get(theme, `colors.${element}-hover`)};
        }

        &:active {
            color: ${get(theme, `colors.${element}-pressed`)};
        }
    `}
`;

/**
 * Since now typography elements can be responsive we need to fetch
 * the theme keys accordingly with this context by convention.
 *
 * @param {*} element eg (heading, body...)
 * @param {*} level eg (1, 2 ...)
 *
 * @example
 * // getResponsiveTokensKeys("body", 3);
 * ["body-3-small", "body-3-medium", "body-3"]
 */
const getResponsiveTokensKeys = (element, level) => [
    // TODO: da sistemare
    // `${element}-${level}-small`,
    // `${element}-${level}-medium`,
    `${element}-${level}`,
];

/**
 * Initial-basic and experimental building block for typography components.
 * All the props are fetched on the theme based on conventions.
 *
 * Since the [Design System](https://zeroheight.com/485b31545/v/0/p/7911cd-stili-tipografici)
 * has multiple levels for each `element`, the theme values and available field also follows
 * these conventions, so they take the prop `element` as a token to identify the corresponding
 * theme value:
 *
 *   - Theme token: `cssField.{element}-{level}`
 *     - `element` can be one of the following: `heading`, `body`, `interactive` and `helper`.
 *     - `level` element can have multiple levels
 *     - `cssField` can be one of the css properties that needs to be styled such as
 *        `fontWeights` or `lineHeights` etc etc
 *
 * The forwarded component props and theme spec can be found [here](https://styled-system.com/table#typography).
 *
 * @example
 * // Use with pre-built theme tokens
 * <Base element="heading" lvl={2} />
 *
 * // Or the underneath styled-system component (should be avoided)
 * <Base fontFamily="Cairo" lineHeight={2} textAlign="center" />
 */
const Base = ({ as, fontFamily, lvl, element, fontWeight, ...rest }) => {
    const children = React.Children.toArray(rest.children);

    const responsiveTokenKeys = getResponsiveTokensKeys(element, lvl);

    return children.map((child, index) => (
        <Typography
            key={index}
            as={as}
            color="text-grey-800"
            element={element}
            fontFamily={fontFamily}
            fontWeight={fontWeight}
            fontSize={responsiveTokenKeys}
            lineHeight={responsiveTokenKeys}
            {...rest}
        >
            {child}
        </Typography>
    ));
};

Base.propTypes = {
    /** html DOM node used by the underneath `styled-component` */
    as: PropTypes.string,
    /** Design System element, this used to fetch theme token values */
    element: PropTypes.oneOf(TYPOGRAPHY_ELEMENTS),
    /** Design System element level */
    lvl: PropTypes.number,
    /** `font-family` value theme alias */
    fontFamily: PropTypes.oneOf(TYPOGRAPHY_FONTS),
    /** `font-weight` value theme alias */
    fontWeight: PropTypes.oneOf(TYPOGRAPHY_WEIGHTS),
};

/**
 * @example
 * // Third-level heading with bold font weight variant.
 * <Heading level={3} weight="semi-bold" />
 */
export const Heading = ({ level, lv = 1, weight, ...rest }) => {
    return <Base as={`h${level ?? lv}`} element="heading" lvl={level ?? lv} fontFamily="regular" fontWeight={weight} {...rest} />;
};

Heading.propTypes = TYPOGRAPHY_COMMON_PROPS;

/**
 * @example
 * // Default level (3) body.
 * <Body level={3} />
 *
 * // Or the shorthand version, with light font weight variant:
 * <Body lv={3} weight="light" />
 */
export const Body = ({ level, lv = 3, weight, ...rest }) => {
    return <Base element="body" lvl={level ?? lv} fontFamily="regular" fontWeight={weight} {...rest} />;
};

Body.propTypes = TYPOGRAPHY_COMMON_PROPS;

/**
 * @example
 * // Second-level helper with default font weight.
 * <Interactive level={2} />
 *
 * // Default helper with light font weight variant.
 * <Interactive weight="light" />
 */
export const Interactive = ({ level, lv = 1, weight, ...rest }) => {
    return <Base as="a" color="enabled-blue" element="interactive" lvl={level ?? lv} fontFamily="regular" fontWeight={weight} {...rest} />;
};

Interactive.propTypes = TYPOGRAPHY_COMMON_PROPS;

/**
 * @example
 * // Default helper.
 * <Helper />
 */
export const Helper = ({ level, lv = 1, weight, ...rest }) => {
    return (
        <Base
            as="span"
            color="gray-800"
            element="helper"
            lvl={level ?? lv}
            fontFamily="regular"
            fontStyle="italic"
            fontWeight={weight}
            {...rest}
        />
    );
};

Helper.propTypes = TYPOGRAPHY_COMMON_PROPS;

export default Typography;
